import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

interface AcceptInvitationProps {
  token: string;
  onComplete: () => void;
}

export const AcceptInvitation: React.FC<AcceptInvitationProps> = ({ token, onComplete }) => {
  const { state } = useAuth();
  const [status, setStatus] = useState<'checking' | 'accepting' | 'success' | 'error'>('checking');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const acceptInvitation = async () => {
      if (!state.isAuthenticated) {
        // Redirect to registration page with invitation token
        console.log('🔄 AcceptInvitation: Redirecting to register with token:', token);
        window.location.href = `/?invitationToken=${token}`;
        return;
      }

      try {
        setStatus('accepting');
        setMessage('Accepting invitation...');

        const response = await apiService.acceptTeamInvitation(token);

        setStatus('success');
        setMessage('Invitation accepted successfully! Redirecting to dashboard...');
        
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          onComplete();
        }, 2000);
      } catch (error: any) {
        console.error('Error accepting invitation:', error);
        setStatus('error');
        setMessage(error.message || 'An error occurred while accepting the invitation. Please try again.');
      }
    };

    acceptInvitation();
  }, [state.isAuthenticated, token, onComplete]);

  const getStatusIcon = () => {
    switch (status) {
      case 'checking':
      case 'accepting':
        return '⏳';
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      default:
        return '🎉';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return '#28a745';
      case 'error':
        return '#dc3545';
      default:
        return '#e67e22';
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      padding: '20px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <div style={{
        background: 'white',
        padding: '40px',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '64px',
          marginBottom: '20px'
        }}>
          {getStatusIcon()}
        </div>

        <h2 style={{
          marginBottom: '20px',
          color: getStatusColor(),
          fontSize: '24px'
        }}>
          {status === 'checking' && 'Checking Invitation...'}
          {status === 'accepting' && 'Accepting Invitation...'}
          {status === 'success' && 'Success!'}
          {status === 'error' && 'Oops!'}
        </h2>

        <p style={{
          marginBottom: '30px',
          lineHeight: '1.6',
          color: '#666',
          fontSize: '16px'
        }}>
          {message}
        </p>

        {status === 'error' && !state.isAuthenticated && (
          <button
            onClick={() => {
              // Redirect to login page while keeping the token in URL
              window.location.href = `/?redirect=/accept-invitation?token=${token}`;
            }}
            style={{
              background: '#e67e22',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            Go to Login
          </button>
        )}

        {status === 'error' && state.isAuthenticated && (
          <button
            onClick={onComplete}
            style={{
              background: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            Go to Dashboard
          </button>
        )}
      </div>
    </div>
  );
};
